/**
 * PhoneModal Component
 * Unified modal that displays phone number with copy/call actions
 * AND outcome options (¿Qué pasó?) in a single view.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PhoneIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  XMarkIcon,
  CheckCircleIcon,
  PhoneXMarkIcon,
  ClockIcon,
  XCircleIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

export type OutcomeType =
  | 'contacted'
  | 'no_answer'
  | 'busy'
  | 'not_interested'
  | 'appointment'
  | 'quote_sent';

interface OutcomeOption {
  id: OutcomeType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
}

const outcomeOptions: OutcomeOption[] = [
  {
    id: 'contacted',
    label: 'Contactado',
    description: 'Hablé con el lead exitosamente',
    icon: CheckCircleIcon,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border-green-200 dark:border-green-800',
  },
  {
    id: 'no_answer',
    label: 'No contestó',
    description: 'Intenté pero no respondió',
    icon: PhoneXMarkIcon,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 border-amber-200 dark:border-amber-800',
  },
  {
    id: 'busy',
    label: 'Llamar después',
    description: 'Ocupado, reagendar contacto',
    icon: ClockIcon,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border-blue-200 dark:border-blue-800',
  },
  {
    id: 'appointment',
    label: 'Cita agendada',
    description: 'Se programó una cita',
    icon: CalendarDaysIcon,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 border-purple-200 dark:border-purple-800',
  },
  {
    id: 'quote_sent',
    label: 'Cotización enviada',
    description: 'Se envió información/cotización',
    icon: DocumentTextIcon,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800',
  },
  {
    id: 'not_interested',
    label: 'No interesado',
    description: 'El lead no tiene interés',
    icon: XCircleIcon,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700',
  },
];

interface PhoneModalProps {
  open: boolean;
  onClose: () => void;
  phone: string;
  name?: string;
  context?: string;
  onOutcomeSelect?: (outcome: OutcomeType, notes?: string) => void;
}

export const PhoneModal: React.FC<PhoneModalProps> = ({
  open,
  onClose,
  phone,
  name,
  context,
  onOutcomeSelect,
}) => {
  const [copied, setCopied] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<OutcomeType | null>(null);
  const [notes, setNotes] = useState('');

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCall = () => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleClose = () => {
    setSelectedOutcome(null);
    setNotes('');
    setCopied(false);
    onClose();
  };

  const handleConfirmOutcome = () => {
    if (!selectedOutcome || !onOutcomeSelect) return;
    onOutcomeSelect(selectedOutcome, notes.trim() || undefined);
    setSelectedOutcome(null);
    setNotes('');
    setCopied(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[99999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center">
              <PhoneIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {name || 'Contacto'}
              </h3>
              {context && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{context}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          {/* Phone Number + Actions */}
          <div className="px-6 py-5">
            <div className="text-center mb-4">
              <p className="text-2xl font-mono font-semibold text-gray-900 dark:text-white tracking-wide">
                {phone}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleCopy}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                  copied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {copied ? (
                  <CheckIcon className="h-5 w-5" />
                ) : (
                  <ClipboardDocumentIcon className="h-5 w-5" />
                )}
                <span className="text-xs font-medium">{copied ? 'Copiado' : 'Copiar'}</span>
              </button>

              <button
                onClick={handleCall}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors"
              >
                <PhoneIcon className="h-5 w-5" />
                <span className="text-xs font-medium">Llamar</span>
              </button>
            </div>
          </div>

          {/* Outcome Section */}
          {onOutcomeSelect && (
            <>
              <div className="mx-6 border-t border-gray-200 dark:border-gray-800" />

              <div className="px-6 py-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  ¿Qué pasó?
                </p>
                <div className="space-y-2">
                  {outcomeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedOutcome === option.id;
                    return (
                      <button
                        key={option.id}
                        onClick={() => setSelectedOutcome(option.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${option.bgColor} ${
                          isSelected ? 'ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-gray-900' : ''
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg bg-white dark:bg-gray-800 ${option.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {option.label}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {option.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notes + Confirm */}
              {selectedOutcome && (
                <div className="px-6 pb-4 space-y-2">
                  <textarea
                    autoFocus
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleConfirmOutcome();
                      }
                    }}
                    placeholder="Notas adicionales (opcional)..."
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                  <button
                    onClick={handleConfirmOutcome}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                    Guardar
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-full py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
